import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBuildingColumns,
  faChevronRight,
  faCircleInfo,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import AppButton from "../../../Components/Common/Button";
import PendingApprovalState, {
  BSL_APPLY_IBAN_STATE,
  BSL_PENDING_APPROVAL_STATE,
} from "../../../Components/Common/PendingApprovalState";
import { BSL_STATUS } from "../../../Components/Sidebar/utils/sidebarStorage";

function MethodCard({
  action,
  disabled = false,
  icon,
  meta,
  onClick,
  title,
  children,
}) {
  const isInteractive = Boolean(onClick) && !disabled;

  return (
    <div
      className={`make-payment-method-card ${disabled ? "is-disabled" : ""}`}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={(event) => {
        if (!isInteractive) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.(event);
        }
      }}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-disabled={disabled ? "true" : undefined}
    >
      <span className="make-payment-method-card__icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>

      <span className="make-payment-method-card__copy">
        <strong>{title}</strong>
        {meta ? <small>{meta}</small> : null}
        {children}
      </span>

      <span className="make-payment-method-card__action">
        {action || <FontAwesomeIcon icon={faChevronRight} />}
      </span>
    </div>
  );
}

export default function PaymentMethodSelector({
  bslStatus,
  canUseBsl = true,
  canUseSuyool = true,
  onApplyIban,
  onBack,
  onSelectBsl,
  onSelectSuyool,
}) {
  const shouldShowBsl = canUseBsl && bslStatus !== BSL_STATUS.rejected;
  const isBslApproved = bslStatus === BSL_STATUS.approved;
  const isBslApply = bslStatus === BSL_STATUS.apply;
  const isBslPending = bslStatus === BSL_STATUS.pending;
  const isBslChecking = bslStatus === null || bslStatus === undefined;
  const hasNoMethods = !shouldShowBsl && !canUseSuyool;

  return (
    <section className="make-payment-methods" aria-labelledby="make-payment-methods-title">
      <div className="make-payment-methods__header">
        {onBack ? (
          <button
            type="button"
            className="make-payment-methods__back"
            onClick={onBack}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        ) : null}
        <h3 id="make-payment-methods-title">Select Method</h3>

      </div>

      <div className="make-payment-methods__list">
        {shouldShowBsl ? (
          <div className="make-payment-methods__group">
            <span className="make-payment-methods__label">Bank Transfer</span>

            {isBslPending ? (
              <PendingApprovalState
                {...BSL_PENDING_APPROVAL_STATE}
                className="make-payment-methods__pending"
              >
                <MethodCard
                  disabled
                  icon={faBuildingColumns}
                  title="BSL Bank Transfer"
                  meta="Expected Arrival: in 2 business days"
                >
                  <span>You will need the beneficiary's bank account details for this method.</span>
                </MethodCard>
              </PendingApprovalState>
            ) : (
              <MethodCard
                disabled={!isBslApproved}
                icon={faBuildingColumns}
                title="BSL Bank Transfer"
                meta={
                  isBslChecking
                    ? "Checking BSL account availability..."
                    : "Expected Arrival: in 2 business days"
                }
                onClick={isBslApproved ? onSelectBsl : undefined}
                action={
                  isBslApply ? (
                    <AppButton
                      type="button"
                      variant="outline"
                      className="make-payment-method-card__apply"
                      onClick={(event) => {
                        event.stopPropagation();
                        onApplyIban?.();
                      }}
                    >
                   Link your BSL Account
                    </AppButton>
                  ) : null
                }
              >
                {isBslApply ? (
                  <span>{BSL_APPLY_IBAN_STATE.message}</span>
                ) : (
                  <span>You will need the beneficiary's bank account details for this method.</span>
                )}
              </MethodCard>
            )}
          </div>
        ) : null}

        {canUseSuyool ? (
          <div className="make-payment-methods__group">
            <span className="make-payment-methods__label">Suyool Transfer</span>
            <MethodCard
              icon={faPaperPlane}
              title="Transfer from Suyool Account."
              meta="Instant Transfer"
              onClick={onSelectSuyool}
            >
              <span>
                You will need the beneficiary's phone number for this method.
                Transfers can be sent to Suyoolers and Non-Suyoolers.
              </span>
            </MethodCard>
          </div>
        ) : null}

        {hasNoMethods ? (
          <div className="make-payment-methods__notice">
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>No payment methods are available for this account yet.</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}
